import React, { useMemo } from 'react';

import type { ShareChannel, ShareConfig } from '../shared/types';

import { useGetShareActionConfig } from './getAction';
import type { ShareActionComponentProps, ShareUrlInfo } from './types';

export type ShareAction<CH extends ShareChannel = ShareChannel> = {
	channel: CH;
	url: string;
	shortUrl?: string;
	// FIXME can we make this type require a specific onClick prop?
	// This currently does not prevent passing components that do not accept onClick
	ShareAction: (props: ShareActionComponentProps) => React.JSX.Element;
};

type Options<CH extends ShareChannel = ShareChannel> = {
	config: ShareConfig;
	trackingEventName: string;
	getUrl: (channel: CH) => string;
	getShortUrlInfo: (channel: CH) => ShareUrlInfo;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onBeforeShare?: (channel: CH) => void;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

export function useShareActionsCore<CH extends ShareChannel = ShareChannel>(
	channels: readonly CH[],
	options: Options<CH>,
): ReadonlyArray<ShareAction<CH>> {
	const getShareActionConfig = useGetShareActionConfig(options);
	const { getUrl, getShortUrlInfo } = options;
	return useMemo(
		() =>
			channels.map((channel) => {
				const url = getUrl(channel);
				const info = getShortUrlInfo(channel);
				const shareActionConfig = getShareActionConfig(channel);

				const ShareAction = (() => {
					if (shareActionConfig.type === 'function') {
						const shareActionFn = shareActionConfig.action;
						const Component = ({
							children,
							onBeforeAction,
							onAfterAction,
							trackingData,
						}: ShareActionComponentProps) => {
							const onClick = useMemo(() => {
								return () => {
									// eslint-disable-next-line max-nested-callbacks
									void (async () => {
										await onBeforeAction?.();
										const success = await shareActionFn(trackingData);
										await onAfterAction?.(success);
									})();
								};
							}, [onBeforeAction, onAfterAction, trackingData]);

							return React.cloneElement(children, {
								...children.props,
								onClick,
							});
						};
						return Component;
					}

					return shareActionConfig.Component;
				})();

				return {
					channel,
					url,
					shortUrl: info.url,
					ShareAction,
				};
			}),
		[channels, getShareActionConfig, getUrl, getShortUrlInfo],
	);
}
